(ns graph-ui.web.ruler
  "A ruler under the top bar, so a sentence about a widget's size can carry a
  NUMBER instead of a gesture.

  ;; @concept CONCEPT-graph-ui-v0-009-01-measure-the-reading-column
  ;; @adr ADR-graph-ui-v0-013-01-one-layout-model
  ;; @feature F-graph-ui-v0-009-01-top-bar-ruler

  George, 2026-08-14: *\"Can you add a tiny ruler with measures to the top bar?
  It seems that we will have to talk a lot about the widget sizes\"*.

  IT READS IN `ch` FIRST, and that is the whole point of it. This project states
  a width as a MEASURE — the sidebar is \"34ch\", the reading column \"66ch\" — because
  a measure stays true when the type size changes and a pixel count does not
  (CONCEPT-graph-ui-v0-009-01). A ruler that could only count pixels would force
  every conversation to convert, so it counts characters and offers pixels as the
  second reading, one click away.

  The ruler starts at the viewport's left edge, which is what makes it usable:
  the sidebar is 34ch wide, so its right edge lands on the 34ch tick. Reading a
  declared measure off the screen is then a matter of looking, not of opening
  devtools and dividing.

  WHAT `ch` IS MEASURED AGAINST, stated because it is the one thing that can
  mislead here: the document body's font. A `ch` in CSS resolves against the font
  of the element it is written on, so a container that sets its own font-size
  resolves 66ch differently from this ruler. The readout says which font it used
  rather than implying there is only one."
  (:require [reagent.core :as r]
            [cljs.reader :as reader]))

;; ---------------------------------------------------------------------------
;; The tick math — pure, so it can be tested without a browser
;; ---------------------------------------------------------------------------

(def scales
  "How often a tick, a taller tick, and a labelled tick fall, per unit.

  The `ch` steps are 1/5/10 because the measures this project argues about are
  two-digit (34, 66) and a tick per character makes them countable. The `px`
  steps are 10/50/100 for the same reason at the scale pixels are quoted in."
  {:ch {:minor 1  :medium 5  :major 10}
   :px {:minor 10 :medium 50 :major 100}})

(def ^:private max-ticks
  "A ruler is decoration past a few hundred marks, and an unbounded `for` over a
  bad measurement is a frozen tab. Bounded rather than trusted."
  2000)

(defn ticks
  "Tick marks for a ruler `width` px wide, `px-per-unit` px to one unit.

  Returns `[{:v unit-value :x px-from-left :kind :minor|:medium|:major :label}]`,
  `:label` only on major ticks. Empty for a width or a scale that cannot be
  drawn — a font that measures 0 happens while a webfont is still loading, and
  an empty ruler is better than one drawn at infinite magnification."
  [unit width px-per-unit]
  (let [{:keys [minor medium major]} (get scales unit)]
    (if (or (nil? minor) (not (pos? width)) (not (pos? px-per-unit)))
      []
      (let [n (min (inc (long (/ width (* minor px-per-unit)))) max-ticks)]
        (into []
              (for [i (range n)
                    :let [v     (* i minor)
                          major? (zero? (mod v major))]]
                (cond-> {:v v
                         :x (* v px-per-unit)
                         :kind (cond major?                  :major
                                     (zero? (mod v medium))  :medium
                                     :else                   :minor)}
                  major? (assoc :label (str v)))))))))

(def ^:private tick-height {:minor 3 :medium 6 :major 10})

;; ---------------------------------------------------------------------------
;; State — which unit, shown or folded, and the measurement
;; ---------------------------------------------------------------------------

(def ^:private storage-key "ikc3.ruler")

(defn- persist! [{:keys [unit shown?]}]
  (try (.setItem js/localStorage storage-key (pr-str {:unit unit :shown? shown?}))
       (catch :default _ nil)))

(defn- restore []
  (try (let [m (some-> (.getItem js/localStorage storage-key) reader/read-string)]
         (when (map? m)
           ;; An unknown unit falls back rather than drawing nothing, the same way
           ;; theme/set-theme! refuses to trust a stale stored key.
           (cond-> (select-keys m [:shown?])
             (contains? scales (:unit m)) (assoc :unit (:unit m)))))
       (catch :default _ nil)))

(defonce state
  (r/atom (merge {:unit :ch :shown? true :px-per-ch nil :width 0} (restore))))

(defn- measure!
  "One character and the viewport, measured on the body so the number means what
  a `ch` written on ordinary content means. Re-measured on resize; a zoom or a
  font swap fires one too."
  []
  (let [el (.createElement js/document "span")]
    (set! (.. el -style -cssText) "position:absolute;visibility:hidden;width:10ch")
    (.appendChild (.-body js/document) el)
    (let [w (.-width (.getBoundingClientRect el))]
      (.remove el)
      (swap! state assoc
             :px-per-ch (when (pos? w) (/ w 10))
             :width (.-innerWidth js/window)))))

(defn toggle!
  "Fold the ruler away, or bring it back. Folded is a real state to want: it is
  an instrument, not chrome, and it costs a row of the window when it is on."
  []
  (persist! (swap! state update :shown? not)))

(defn- switch-unit! []
  (persist! (swap! state update :unit #(if (= :ch %) :px :ch))))

(defn shown? [] (:shown? @state))

;; ---------------------------------------------------------------------------
;; The strip
;; ---------------------------------------------------------------------------

(defonce ^:private on-resize (fn [_] (measure!)))

(defn- readout
  "The conversion, and the unit switch. It sits on an opaque background because
  ticks running under small type make both unreadable."
  [unit px-per-ch width]
  [:div {:class "absolute right-0 top-0 h-full flex items-center gap-2 pl-2 pr-1.5 text-[9px] font-mono cursor-pointer"
         :style {:background "var(--panel)" :color "var(--faint)"}
         :title (str "Ruler in " (name unit) " — click to switch to "
                     (if (= :ch unit) "pixels" "characters") ".\n"
                     "ch is measured on the document body font; an element that sets its own "
                     "font-size resolves ch differently.")
         :on-click switch-unit!}
   (when px-per-ch
     [:span (str "1ch = " (.toFixed px-per-ch 1) "px")])
   [:span {:style {:color "var(--muted)"}}
    (if (and px-per-ch (pos? px-per-ch))
      (str (Math/round (/ width px-per-ch)) "ch · " (Math/round width) "px")
      (str (Math/round width) "px"))]
   [:span {:style {:color "var(--accent)"}} (name unit)]])

(defn ruler
  "The strip itself: ticks from the viewport's left edge, labels on the tens, and
  the conversion at the right. Height is a hair over the tallest tick — `tiny`
  was the request, and a ruler that costs real vertical space would be turned off
  and never used."
  []
  (r/create-class
   {:display-name "top-bar-ruler"
    :component-did-mount
    (fn [_] (measure!) (.addEventListener js/window "resize" on-resize))
    :component-will-unmount
    (fn [_] (.removeEventListener js/window "resize" on-resize))
    :reagent-render
    (fn []
      (let [{:keys [unit shown? px-per-ch width]} @state]
        (when shown?
          (let [ppu (if (= :px unit) 1 px-per-ch)
                ts  (ticks unit width (or ppu 0))]
            ;; CHILDREN SPREAD POSITIONALLY, never handed back as one nested
            ;; vector: a ClojureScript vector implements IFn, so Reagent reads a
            ;; vector-of-pieces in a single child slot as a component call and
            ;; dies with `Invalid arity` once real data arrives. `into` + `concat`
            ;; is the shape widget-box uses for the same reason.
            (into [:div {:class "relative flex-shrink-0 overflow-hidden select-none"
                         :style {:height "16px"
                                 :background "var(--panel)"
                                 :border-bottom "1px solid var(--border)"}}]
                  (concat
                   (for [{:keys [x kind v]} ts]
                     ^{:key (str "t" v)}
                     [:div {:style {:position "absolute" :bottom 0 :left (str x "px")
                                    :width "1px" :height (str (tick-height kind) "px")
                                    :background (if (= :major kind) "var(--muted)" "var(--border)")}}])
                   (for [{:keys [x label v]} (filter :label ts)]
                     ^{:key (str "l" v)}
                     [:div {:class "absolute font-mono"
                            :style {:bottom "9px" :left (str (+ x 2) "px")
                                    :font-size "8px" :line-height 1
                                    :color "var(--faint)"}}
                      label])
                   [[readout unit px-per-ch width]]))))))}))
